import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Archive,
  ChevronDown,
  ChevronRight,
  Download,
  Edit,
  FileText,
  GitBranch,
  Info,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { useEngagementAuditQuery, type AuditEvent, type ListAuditOpts } from '@/lib/api/audit';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { AuditDiff } from '@/components/audit/audit-diff';
import { ArchiveModal } from '@/components/modals/archive-modal';
import { avatarTone, deriveInitials } from '@/components/clients/team-avatars';
import { auditOperationLabel, describeAuditEvent } from '@/lib/audit-text';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';

/**
 * Audit trail screen (C20-140 / E01-F05).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `Screens.audit`. Header + meta line, "Build engagement archive" action,
 * filter row card (event type / actor / date range / "Showing N of total" +
 * Export view), then a card containing 4-column rows (timestamp · actor ·
 * kind icon · prose). Clicking a row expands the JSON diff (no prototype-
 * level pattern — kept from prior implementation because it's the only way
 * to surface the structured before/after the audit-worker writes).
 *
 * Wire-up:
 *   - rows  : GET /api/v1/engagements/:id/audit (T-0015 + T-0023 backend).
 *   - actor : GET /api/v1/users/lookup?ids=... (T-0127) for display name +
 *             avatar tone. AI-emitted rows (actor_user_id NULL) render the
 *             `chip-ai` "AI" pill matching the prototype.
 *   - prose : `describeAuditEvent` (`lib/audit-text.ts`), a sentence built
 *             from the operation, the record's kind and its name
 *             (`resource_title`), with the stored operation as the tooltip
 *             (C2-30). No ids in the sentence; the expanded row has them.
 *   - archive: opens the existing `<ArchiveModal>` (T-0115).
 *   - export : client-side CSV download of the loaded page (no backend
 *             export endpoint exists; surfaced honestly as "Export view").
 *
 * Backend gaps surfaced explicitly:
 *   - The audit endpoint accepts only `event_type` in {create, update,
 *     delete, access_denied, override, proposal}. Prototype shows kinds
 *     like "Snapshot / Re-evaluation / RFI / Document upload" — those
 *     are *resource categorisations*, not operations. We map between the
 *     two via `kindFromEvent()`; the filter dropdown sends the backend
 *     event-type enum.
 *   - The list endpoint does not return a "Showing N of total" counter
 *     unless data has loaded; pre-load it shows em-dash.
 *   - No /export endpoint — Export view is intentionally a client CSV.
 *   - Actor dropdown options are populated from actors visible on the
 *     current page (no global directory endpoint exists; safer than
 *     hardcoding names).
 */

export const Route = createFileRoute('/engagements/$engagementId/audit')({
  component: AuditTab,
});

type EventTypeFilter = ListAuditOpts['event_type'] | '';

const EVENT_TYPE_OPTIONS: Array<{ value: NonNullable<EventTypeFilter>; label: string }> = [
  { value: 'override', label: 'Override' },
  { value: 'proposal', label: 'AI proposal' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'access_denied', label: 'Access denied' },
];

type DateRangeOption = '7' | '30' | '90' | 'all';

const DATE_RANGE_OPTIONS: Array<{ value: DateRangeOption; label: string }> = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'Engagement to date' },
];

const PAGE_SIZE = 50;

function fromIsoForRange(range: DateRangeOption): string | undefined {
  if (range === 'all') return undefined;
  const days = Number(range);
  if (!Number.isFinite(days)) return undefined;
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

function AuditTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/audit' });
  const [eventType, setEventType] = useState<EventTypeFilter>('');
  const [actorId, setActorId] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRangeOption>('7');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const apiFilters: ListAuditOpts = useMemo(() => {
    const out: ListAuditOpts = { page, page_size: PAGE_SIZE };
    if (eventType !== '') out.event_type = eventType;
    if (actorId !== '') out.actor_user_id = actorId;
    const from = fromIsoForRange(dateRange);
    if (from) out.from = from;
    return out;
  }, [eventType, actorId, dateRange, page]);

  const list = useEngagementAuditQuery(engagementId, apiFilters);
  const events = list.data?.events ?? [];

  // Resolve actor display names + avatar tones via the users-lookup endpoint
  // (T-0127). Page-scoped, deduped, capped at 100 ids server-side.
  const actorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of events) {
      if (e.actor_user_id) ids.add(e.actor_user_id);
    }
    return Array.from(ids);
  }, [events]);
  const actorLookup = useUsersLookupQuery(actorIds);
  const actorById = useMemo(() => {
    const m = new Map<string, { display_name: string; avatar_color_index: number }>();
    for (const item of actorLookup.data?.items ?? []) {
      m.set(item.id, {
        display_name: item.display_name,
        avatar_color_index: item.avatar_color_index,
      });
    }
    return m;
  }, [actorLookup.data]);

  // Build the actor dropdown options from actors present on this page. This
  // is an explicit limitation surfaced in the comment block at the top —
  // there is no global "list every actor on this engagement's audit" API.
  const actorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of events) {
      if (!e.actor_user_id) continue;
      if (seen.has(e.actor_user_id)) continue;
      seen.set(e.actor_user_id, actorById.get(e.actor_user_id)?.display_name ?? UNKNOWN_USER);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [events, actorById]);

  const total = list.data?.total ?? 0;
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  function handleExport() {
    if (events.length === 0) return;
    const header = [
      'timestamp',
      'actor_user_id',
      'actor_name',
      'description',
      'event_type',
      'resource_type',
      'resource_id',
      'correlation_id',
      'override_tag',
    ];
    const lines = [header.join(',')];
    for (const e of events) {
      const actorName = e.actor_user_id
        ? (actorById.get(e.actor_user_id)?.display_name ?? '')
        : 'AI';
      lines.push(
        [
          e.created_at,
          e.actor_user_id ?? '',
          csvEscape(actorName),
          csvEscape(describeAuditEvent(e)),
          e.event_type,
          e.resource_type,
          e.resource_id,
          e.correlation_id,
          csvEscape(e.override_tag ?? ''),
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-${engagementId.slice(0, 8)}-page-${page}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-3.5">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Audit trail</h1>
          <p className="text-meta text-ink-500">
            Every edit retained for the life of the engagement. Exportable to engagement archive.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setArchiveOpen(true)}
            data-open-archive={engagementId}
          >
            <Archive className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Build engagement archive
          </Button>
        </div>
      </div>

      <div className="rounded-card border border-ink-100 bg-paper-50 px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-meta font-semibold text-ink-700">Filters</span>
          <select
            aria-label="Event type"
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value as EventTypeFilter);
              setPage(1);
            }}
            className="h-[30px] w-40 rounded border border-ink-200 bg-paper-0 px-2 text-meta text-ink-700"
          >
            <option value="">All event types</option>
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Actor"
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value);
              setPage(1);
            }}
            className="h-[30px] w-40 rounded border border-ink-200 bg-paper-0 px-2 text-meta text-ink-700"
          >
            <option value="">Any actor</option>
            {actorOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Date range"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value as DateRangeOption);
              setPage(1);
            }}
            className="h-[30px] w-36 rounded border border-ink-200 bg-paper-0 px-2 text-meta text-ink-700"
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="ml-auto text-meta text-ink-500">
            {list.data ? (
              <>
                Showing <strong className="text-ink-700">{events.length}</strong> of{' '}
                <strong className="text-ink-700">{total.toLocaleString()}</strong> events
              </>
            ) : (
              <>Loading…</>
            )}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={events.length === 0}
            onClick={handleExport}
          >
            <Download className="mr-1.5 h-3 w-3" aria-hidden="true" />
            Export view
          </Button>
        </div>
      </div>

      <div className="rounded-card border border-ink-100 bg-paper-0">
        {list.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-body-sm text-ink-500">
            <Spinner /> Loading audit events…
          </div>
        ) : list.error ? (
          <div className="p-6">
            <ErrorMessage error={list.error} />
          </div>
        ) : events.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="No audit events"
              description="No events match the current filters. Widen the date range or clear filters."
            />
          </div>
        ) : (
          <ul role="list" className="divide-y divide-ink-100">
            {events.map((event) => (
              <AuditRow
                key={event.id}
                event={event}
                actor={event.actor_user_id ? actorById.get(event.actor_user_id) : undefined}
                expanded={expanded === event.id}
                onToggle={() => setExpanded((prev) => (prev === event.id ? null : event.id))}
              />
            ))}
          </ul>
        )}
      </div>

      {list.data && list.data.total > list.data.page_size ? (
        <div className="flex items-center justify-between">
          <span className="text-meta text-ink-500">
            Showing {showingFrom.toLocaleString()} to {showingTo.toLocaleString()} of{' '}
            {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <ArchiveModal engagementId={engagementId} open={archiveOpen} onOpenChange={setArchiveOpen} />
    </div>
  );
}

interface ActorMeta {
  display_name: string;
  avatar_color_index: number;
}

/** Shown until the users lookup names an actor, or for a user who no longer exists. */
const UNKNOWN_USER = 'Unknown user';

function AuditRow({
  event,
  actor,
  expanded,
  onToggle,
}: {
  event: AuditEvent;
  actor: ActorMeta | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const kind = kindFromEvent(event);
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="grid w-full grid-cols-[160px_140px_36px_1fr_20px] items-start gap-4 px-5 py-3.5 text-left transition-colors hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
      >
        <div className="font-mono text-mono-sm text-ink-700">
          {formatDateTime(event.created_at)}
        </div>
        <div className="flex items-center gap-2 text-body-sm">
          {event.actor_user_id ? (
            <>
              <ActorAvatar
                userId={event.actor_user_id}
                name={actor?.display_name}
                tone={actor?.avatar_color_index}
              />
              <span className="truncate text-ink-700">{actor?.display_name ?? UNKNOWN_USER}</span>
            </>
          ) : (
            <>
              <span className="inline-flex h-[18px] items-center rounded-full border border-ai-surface-border bg-ai-surface px-2 text-[10px] font-medium text-ai-ink">
                AI
              </span>
              <span className="text-ink-700">System</span>
            </>
          )}
        </div>
        <div>
          <KindIcon kind={kind} />
        </div>
        <div className="text-body-sm leading-5 text-ink-700">
          <EventDescription event={event} />
        </div>
        <div className="pt-1 text-ink-500" aria-hidden="true">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>
      {expanded ? (
        <div className="space-y-3 px-5 pb-4 pt-1">
          <div className="grid grid-cols-1 gap-2 text-meta text-ink-500 md:grid-cols-2">
            <div>
              <span className="font-medium text-ink-700">Recorded as:</span>{' '}
              <code className="font-mono">{auditOperationLabel(event)}</code>
            </div>
            <div>
              <span className="font-medium text-ink-700">Record id:</span>{' '}
              <span className="font-mono">{event.resource_id}</span>
            </div>
            <div>
              <span className="font-medium text-ink-700">Correlation id:</span>{' '}
              <span className="font-mono">{event.correlation_id}</span>
            </div>
            {event.source_event_topic ? (
              <div>
                <span className="font-medium text-ink-700">Source topic:</span>{' '}
                <span className="font-mono">{event.source_event_topic}</span>
              </div>
            ) : null}
            {event.override_tag ? (
              <div>
                <span className="font-medium text-ink-700">Override tag:</span>{' '}
                <span className="font-mono">{event.override_tag}</span>
              </div>
            ) : null}
            {event.override_rationale ? (
              <div className="md:col-span-2">
                <span className="font-medium text-ink-700">Rationale:</span>{' '}
                <span>{event.override_rationale}</span>
              </div>
            ) : null}
          </div>
          <AuditDiff before={event.before} after={event.after} />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Six "kind" buckets that map onto the prototype's icon set. The backend's
 * `event_type` is the canonical {create, update, delete, access_denied,
 * override, proposal} enum, plus a second signal (`resource_type`) that lets
 * us split off snapshots / RFIs / documents into their own icon. Returning
 * `kind` here is the only place where backend taxonomy translates into UI
 * vocabulary; downstream consumers (icon + description) use it freely.
 */
type AuditKind = 'snapshot' | 'ai-proposal' | 'override' | 'edit' | 'reeval' | 'rfi' | 'doc';

function kindFromEvent(event: AuditEvent): AuditKind {
  const t = event.resource_type.toLowerCase();
  if (t === 'snapshot') return 'snapshot';
  if (t === 'rfi' || t.startsWith('rfi_')) return 'rfi';
  if (t === 'document' || t === 'document_chunk') return 'doc';
  if (t === 'reevaluation' || t === 'ai_reeval_job') return 'reeval';
  if (event.event_type === 'override') return 'override';
  if (event.event_type === 'proposal' || event.event_type === 'proposal_decide') {
    return 'ai-proposal';
  }
  if (event.actor_user_id === null) return 'ai-proposal';
  return 'edit';
}

const KIND_LABEL: Record<AuditKind, string> = {
  snapshot: 'Snapshot',
  'ai-proposal': 'AI proposal',
  override: 'Override',
  edit: 'Edit',
  reeval: 'Re-evaluation',
  rfi: 'RFI',
  doc: 'Document',
};

function KindIcon({ kind }: { kind: AuditKind }) {
  // Tone mapping mirrors `screens-engagement.js → kindIcon()`:
  //   snapshot → ink-700 ;  override → posture-at-risk
  //   ai-proposal / reeval → ai-ink ; edit → ink-500
  //   rfi → ink-700 ; doc → ink-500
  const map: Record<AuditKind, { Icon: typeof GitBranch; toneClass: string }> = {
    snapshot: { Icon: GitBranch, toneClass: 'text-ink-700' },
    'ai-proposal': { Icon: Info, toneClass: 'text-ai-ink' },
    override: { Icon: Edit, toneClass: 'text-posture-at-risk' },
    edit: { Icon: Edit, toneClass: 'text-ink-500' },
    reeval: { Icon: Info, toneClass: 'text-ai-ink' },
    rfi: { Icon: MessageSquare, toneClass: 'text-ink-700' },
    doc: { Icon: FileText, toneClass: 'text-ink-500' },
  };
  const { Icon, toneClass } = map[kind];
  return (
    <span
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded-md bg-paper-100',
        toneClass,
      )}
      aria-label={KIND_LABEL[kind]}
      title={KIND_LABEL[kind]}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
}

/**
 * The event as a sentence ("Approved contract fact: Vendor") from the shared
 * formatter, with the stored operation in the tooltip for auditors who know
 * the log's vocabulary (C2-30). An override keeps its tag chip and reason.
 */
function EventDescription({ event }: { event: AuditEvent }) {
  return (
    <span title={auditOperationLabel(event)}>
      {describeAuditEvent(event)}.
      {event.override_tag ? (
        <>
          {' '}
          <span className="inline-flex h-[18px] items-center rounded-full bg-ink-100 px-2 text-[10px] font-medium text-ink-700">
            {event.override_tag}
          </span>
        </>
      ) : null}
      {event.override_rationale ? (
        <span className="text-ink-500"> Reason: &ldquo;{event.override_rationale}&rdquo;</span>
      ) : null}
    </span>
  );
}

function ActorAvatar({
  userId,
  name,
  tone,
}: {
  userId: string;
  name: string | undefined;
  tone: number | undefined;
}) {
  // Prefer the server-derived avatar_color_index (T-0127); fall back to a
  // local hash so unknown actors still render a stable disk.
  const member = { id: userId, name: name ?? userId };
  const fallback = avatarTone(member);
  const idx = tone ?? fallback;
  // An actor the lookup can't name gets a neutral disk, not id characters.
  const initials = name ? deriveInitials(name) : '?';
  return (
    <span
      title={name ?? UNKNOWN_USER}
      aria-label={name ?? UNKNOWN_USER}
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-full border border-paper-0 font-mono text-[9px] font-semibold',
        `bg-avatar-${idx}-bg`,
        `text-avatar-${idx}-ink`,
      )}
    >
      {initials}
    </span>
  );
}

function csvEscape(s: string): string {
  if (s === '') return '';
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export { AuditTab };
